import BigNumber from 'bignumber.js'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { Skeleton } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { mediaMinTablet } from '@root/shared/ui-kit'

import type { ClassNameProps } from '@root/shared/react'

type Props = {
  amount: BigNumber | undefined
  currencyPriceUsd: AsyncStateN.AsyncStateN<unknown, BigNumber>
} & ClassNameProps

export const CurrencyInputUsdtAmount: React.FC<Props> = ({
  amount,
  currencyPriceUsd,
}) => {
  const { t } = useTranslation()

  const tokenPriceState = useMemo(
    () =>
      pipe(
        currencyPriceUsd,
        AsyncStateN.map((currencyPrice) => {
          if (!amount) {
            return BigNumber(0)
          }
          return currencyPrice.multipliedBy(amount)
        }),
      ),
    [amount, currencyPriceUsd],
  )

  if (AsyncStateN.isError(tokenPriceState)) {
    return <AmountUsdt>{t('common.n_a')}</AmountUsdt>
  }

  if (amount?.isZero()) {
    return <AmountUsdt>$0</AmountUsdt>
  }

  if (AsyncStateN.isSuccess(tokenPriceState)) {
    if (amount === undefined) {
      return <></>
    }

    const price = tokenPriceState.value

    return (
      <AmountUsdt>
        <Numeric value={price} preset="currency-amount-short" currency="$" />
      </AmountUsdt>
    )
  }

  return <Skeleton height={15} paddingBlock={2} width={40} />
}

const AmountUsdt = styled.div`
  color: var(--text-8);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  ${mediaMinTablet(css`
    font-size: 11px;
  `)}
`
